import React, { useState } from 'react';
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
} from '@mui/material';
import {
  HomeOutlined as HomeIcon,
  CampaignOutlined as CampaignIcon,
  SettingsOutlined as SettingsIcon,
  HelpOutlineOutlined as HelpIcon,
  InfoOutlined as InfoIcon,
  AdminPanelSettingsOutlined as AdminIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { AppColors } from '../theme/colors';
import { PostTypeSelectDialog, PostType } from './PostTypeSelectDialog';
import { useAuth, useCompany } from '../hooks/useAuth';

interface DesktopSidebarProps {
  onPostCreated?: () => void;
}

/**
 * PC版用の固定サイドバー
 */
export const DesktopSidebar: React.FC<DesktopSidebarProps> = ({ onPostCreated }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { company } = useCompany(user?.uid || null);
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const handleNewPostClick = () => {
    setTypeDialogOpen(true);
  };

  const handleSelectType = (type: PostType) => {
    setTypeDialogOpen(false);
    navigate(`/post/new?type=${type}`);
  };

  return (
    <Box
      sx={{
        width: 240,
        height: '100vh',
        bgcolor: 'white',
        borderRight: '1px solid #e0e0e0',
        position: 'fixed',
        top: 0,
        left: 0,
        pt: '64px', // ヘッダーの高さ分のpadding
        overflowY: 'auto',
      }}
    >
      <List sx={{ pt: 2 }}>
        <ListItemButton 
          onClick={() => handleNavigation('/home')}
          selected={location.pathname === '/home'}
        >
          <ListItemIcon>
            <HomeIcon sx={{ color: AppColors.primary }} />
          </ListItemIcon>
          <ListItemText primary="ホーム" />
        </ListItemButton>
        <ListItemButton 
          onClick={handleNewPostClick}
          selected={typeDialogOpen}
        >
          <ListItemIcon>
            <CampaignIcon sx={{ color: AppColors.primary }} />
          </ListItemIcon>
          <ListItemText primary="新規投稿" />
        </ListItemButton>
        <Divider sx={{ my: 1 }} />
        <ListItemButton 
          onClick={() => handleNavigation('/settings')}
          selected={location.pathname === '/settings'}
        >
          <ListItemIcon>
            <SettingsIcon sx={{ color: AppColors.primary }} />
          </ListItemIcon>
          <ListItemText primary="設定" />
        </ListItemButton>
        <ListItemButton 
          onClick={() => handleNavigation('/help')}
          selected={location.pathname === '/help'}
        >
          <ListItemIcon>
            <HelpIcon sx={{ color: AppColors.primary }} />
          </ListItemIcon>
          <ListItemText primary="ヘルプ" />
        </ListItemButton>
        <ListItemButton 
          onClick={() => handleNavigation('/about-app')}
          selected={location.pathname === '/about-app'}
        >
          <ListItemIcon>
            <InfoIcon sx={{ color: AppColors.primary }} />
          </ListItemIcon>
          <ListItemText primary="アプリについて" />
        </ListItemButton>
        <Divider sx={{ my: 1 }} />
        <ListItemButton 
          onClick={() => handleNavigation('/admin-login')}
          selected={location.pathname === '/admin-login'}
        >
          <ListItemIcon>
            <AdminIcon sx={{ color: AppColors.primary }} />
          </ListItemIcon>
          <ListItemText primary="管理画面ログイン" />
        </ListItemButton>
      </List>

      <PostTypeSelectDialog
        open={typeDialogOpen}
        onClose={() => setTypeDialogOpen(false)}
        onSelect={handleSelectType}
        industryType={company?.industryType}
      />
    </Box>
  );
};
